import { gql, useMutation } from '@apollo/client';
import { Box, Typography, Divider, Stack, CardOverflow, Button, FormControl, Input, FormLabel, Chip } from '@mui/joy';
import { Card, CardActions } from '@mui/joy';
import { useEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useFeedback } from './feedback/Service';
import { parseGraphQLError } from '../utils';
import useUser from './auth/useUser';
import { ATSConnection } from './integrations';
import { getProvider } from './integrations/providers';

const CREATE_CLIENT = gql`
  mutation CreateClient($client: ClientInput!) {
    createClient(client: $client) {
      _id
    }
  }
`;

const UPDATE_ATS_DECISION = gql`
  mutation UpdateATSDecision($usesATS: Boolean!) {
    updateATSDecision(usesATS: $usesATS) {
      _id
      usesATS
      selectedATSProvider
    }
  }
`;

let initialState = {
  company: "",
  comment: "",
  // website: "",
  // funding: "",
  // revenue: "",
  // industry: "",
  // culture: "",
  // linkedin: "",
  // facebook: "",
  // twitter: "",
  // instagram: "",
  // customLink: "",
  // employeesNumber: "",
  // salesNumber: "",
}

// const numericFields = [];

const reducer = (state, newState) => ({
  ...state,
  ...newState
})

const Company = () => {
  const [state, setState] = useReducer(reducer, initialState);
  const feedback = useFeedback();
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const isOnboarding = location.pathname === '/start-company';

  const [submit, { loading }] = useMutation(CREATE_CLIENT, {
    update(cache, { data: { createClient } }) {
      cache.modify({
        id: cache.identify({ _id: user._id, __typename: "User" }),
        fields: {
          client: () => createClient,
        }
      });
    }
  });

  const [updateDecision, { loading: updatingDecision }] = useMutation(UPDATE_ATS_DECISION, {
    update(cache, { data: { updateATSDecision } }) {
      cache.modify({
        id: cache.identify({ _id: user._id, __typename: 'User' }),
        fields: {
          client: (existing) => ({
            ...existing,
            usesATS: updateATSDecision.usesATS,
            selectedATSProvider: updateATSDecision.selectedATSProvider,
          }),
        },
      });
    },
    onError: (error) => {
      feedback.snackbar({ text: parseGraphQLError(error), type: 'error' });
    },
  });

  const handleATSDecisionChange = async (usesATS) => {
    await updateDecision({ variables: { usesATS } });
    if (usesATS) {
      navigate('/onboarding/ats-selector');
    } else {
      feedback.snackbar({ text: 'ATS preference updated.', type: 'success' });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const data = { ...state };
      // for (let field of numericFields) {
      //   if (typeof data[field] === "object") {
      //     data[field] = { from: parseFloat(data[field].from), to: parseFloat(data[field].to) };
      //     if (data[field].from > data[field].to)
      //       return;
      //   } else
      //     data[field] = parseFloat(data[field]);
      // }
      console.log(data);
      await submit(({ variables: { client: data } }));
      feedback.snackbar({ text: "Changes saved!", type: "success" });
      if (!user.client) {
        window.gtag('event', 'client_profile', {});
      }
      // if (redirect)
      // navigate(redirect);
    } catch (error) {
      console.error("ClientForm error", error);
      feedback.snackbar({ text: parseGraphQLError(error), type: "error" });
    }
    // console.log(talent);
  }

  const handleChange = ({ target: { name, value, checked, type } }) => {
    setState({ [name]: type === "checkbox" ? checked : value /* (type === "number" ? parseFloat(value) : value) */ });
  }

  useEffect(() => {
    if (user.client) {
      setState({
        ...user.client,
        createdAt: undefined,
        updatedAt: undefined,
        jobs: undefined,
        __typename: undefined,
        myRole: undefined,
        candidatesStats: undefined,
        credits: undefined,
        currentTierId: undefined,
      });
    }
  }, [user]);

  return (
    <Box
      component="main"
      sx={{
        my: 'auto',
        py: 2,
        pb: 5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: "100%",
        maxWidth: 'sm',
        mx: 'auto',
        borderRadius: 'sm',
        '& form': {
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        },
        // [`& .MuiFormLabel-asterisk`]: {
        //   visibility: 'hidden',
        // },
      }}
    >
      <Card>
        <Box sx={{ mb: 1 }}>
          <Typography level="title-md" gutterBottom>Company</Typography>
          <Typography level="body-sm">
            General information about your company.
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2} sx={{ my: 1 }} component={"form"} id='company-form' onSubmit={handleSubmit}>
          <FormControl required>
            <FormLabel>{"What's your company name?"}</FormLabel>
            <Input size="sm" placeholder="Name" value={state.company} onChange={handleChange} name="company" />
          </FormControl>
          {/* <FormControl>
            <FormLabel>Description</FormLabel>
            <Textarea
              size="sm"
              minRows={4}
              value={state.comment}
              onChange={handleChange}
              name="comment"
              placeholder="Description"
            />
          </FormControl> */}
        </Stack>
        <CardOverflow sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
          <CardActions sx={{ alignSelf: 'flex-end', pt: 2 }}>
            {/* <Button size="sm" variant="outlined" color="neutral">
              Cancel
            </Button> */}
            <Button size="sm" variant="solid" color="primary" type='submit' form='company-form' loading={loading}>
              {isOnboarding ? "Next" : "Save"}
            </Button>
          </CardActions>
        </CardOverflow>
      </Card>

      {user.client?.selectedATSProvider === 'ashby' && (
        <ATSConnection providerId="ashby" />
      )}
      {user.client?.selectedATSProvider === 'greenhouse' && (
        <ATSConnection providerId="greenhouse" />
      )}

      {/* ATS Decision section — visible on settings page only */}
      {!isOnboarding && user.client && (
        <Card>
          <Box sx={{ mb: 1 }}>
            <Typography level="title-md" gutterBottom>ATS Setup</Typography>
            <Typography level="body-sm">
              {user.client.usesATS === null
                ? 'You haven\'t set an ATS preference yet.'
                : user.client.usesATS
                  ? `You use an ATS${user.client.selectedATSProvider ? ` (${getProvider(user.client.selectedATSProvider)?.name ?? user.client.selectedATSProvider})` : ''}.`
                  : 'You manage candidates without an ATS.'}
            </Typography>
          </Box>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
            {user.client.usesATS !== false && (
              <Button
                size="sm"
                variant={user.client.usesATS === false ? 'solid' : 'outlined'}
                color="neutral"
                loading={updatingDecision}
                onClick={() => handleATSDecisionChange(false)}
              >
                Switch to no ATS
              </Button>
            )}
            {user.client.usesATS !== true && (
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                loading={updatingDecision}
                onClick={() => handleATSDecisionChange(true)}
              >
                Switch to ATS
              </Button>
            )}
            {user.client.usesATS === true && (
              <Button
                size="sm"
                variant="outlined"
                color="primary"
                loading={updatingDecision}
                onClick={() => navigate('/onboarding/ats-selector')}
              >
                Change ATS provider
              </Button>
            )}
            {user.client.usesATS === null && (
              <>
                <Button
                  size="sm"
                  variant="outlined"
                  color="primary"
                  loading={updatingDecision}
                  onClick={() => handleATSDecisionChange(true)}
                >
                  I use an ATS
                </Button>
                <Button
                  size="sm"
                  variant="outlined"
                  color="neutral"
                  loading={updatingDecision}
                  onClick={() => handleATSDecisionChange(false)}
                >
                  I don&apos;t use an ATS
                </Button>
              </>
            )}
          </Stack>
          {user.client.usesATS !== null && (
            <Box sx={{ mt: 1 }}>
              <Chip size="sm" color={user.client.usesATS ? 'primary' : 'neutral'} variant="soft">
                {user.client.usesATS ? 'Uses ATS' : 'No ATS'}
              </Chip>
            </Box>
          )}
        </Card>
      )}
    </Box>
  );
}

export default Company;